import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useMall } from "@/context/MallContext";

export const money = (n) => {
    const v = Number(n || 0);
    return Number.isInteger(v) ? `$${v.toLocaleString()}` : `$${v.toFixed(2)}`;
};

export const btnPrimary = "flex min-h-[48px] w-full items-center justify-center border border-ink bg-accent-brand px-4 text-[16px] font-semibold text-on-accent disabled:opacity-50";
export const btnChip = (on) => `flex min-h-[44px] items-center justify-center border px-3 text-[16px] ${on ? "border-accent-brand border-2 font-semibold" : "border-ink"}`;
export const input = "min-h-[44px] w-full border border-ink bg-field px-3 text-[16px] text-ink";

/** One block of the product page: a 20px title and whatever sits under it. */
export function Section({ title, children, testid }) {
    return (
        <section className="border-t border-line px-3 py-4" data-testid={testid}>
            {title && <h2 className="text-[20px] font-semibold leading-tight">{title}</h2>}
            <div className={title ? "mt-3" : ""}>{children}</div>
        </section>
    );
}

/** Square quantity chips with a type-your-own box at the end. */
export function QtyPicker({ presets = [], value, onChange, min = 1, testid = "qty" }) {
    const [custom, setCustom] = useState(presets.includes(Number(value)) ? "" : String(value || ""));
    useEffect(() => { if (presets.includes(Number(value))) setCustom(""); }, [value, presets]);
    return (
        <div className="grid grid-cols-3 gap-2" data-testid={`${testid}-picker`}>
            {presets.map((q) => (
                <button key={q} type="button" onClick={() => onChange(q)} className={btnChip(Number(value) === q && custom === "")}
                    data-testid={`${testid}-preset-${q}`}>{q.toLocaleString()}</button>
            ))}
            <input type="number" inputMode="numeric" min={min} placeholder="Other" value={custom} aria-label="Other quantity"
                onChange={(e) => { setCustom(e.target.value); const n = Number(e.target.value); if (n >= min) onChange(n); }}
                className={input} data-testid={`${testid}-custom`} />
        </div>
    );
}

/** 5.1 #4 — shown only once a file exists. */
export function FileCheck({ check, cleanup, setCleanup, fee }) {
    if (!check) return null;
    if (check.ok) {
        return <p className="px-3 py-3 text-[16px]" data-testid="file-check-ok">File looks good.</p>;
    }
    return (
        <div className="px-3 py-3" data-testid="file-check-warn">
            <p className="text-[16px]">This file may print blurry.</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setCleanup(true)} className={btnChip(cleanup)} data-testid="file-check-cleanup">
                    Clean it up (+{money(fee)})
                </button>
                <button type="button" onClick={() => setCleanup(false)} className={btnChip(!cleanup)} data-testid="file-check-asis">
                    Print as is (free)
                </button>
            </div>
        </div>
    );
}

export function NoteField({ value, onChange, placeholder }) {
    return (
        <Section title="Anything else? (optional)" testid="note-section">
            <textarea value={value} maxLength={1000} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                rows={3} className={`${input} py-2`} data-testid="line-note" />
        </Section>
    );
}

/** 5.1 #6 — the live ready-by line, with the optional Need-it-by date beside it. */
export function ReadyBy({ store, readyIn, extraDays, fallback, needBy, setNeedBy }) {
    const [ready, setReady] = useState(fallback || null);
    useEffect(() => {
        api.get("/simple/ready-by", { params: { store: store?.slug, ready_in: readyIn || "weeks_2_4", extra_days: extraDays || 0 } })
            .then(({ data }) => setReady(data)).catch(() => { });
    }, [store?.slug, readyIn, extraDays]);
    const sooner = needBy && ready?.ready_by && needBy < ready.ready_by;
    return (
        <Section testid="ready-section">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <p className="text-[16px] font-semibold" data-testid="ready-by-line">{ready?.text || "…"}</p>
                <label className="flex flex-col gap-1 text-[14px] text-muted-ink">
                    Need it by (optional)
                    <input type="date" value={needBy || ""} onChange={(e) => setNeedBy(e.target.value)} className={`${input} sm:w-56`} data-testid="need-by" />
                </label>
            </div>
            {sooner && store?.phone && (
                <p className="mt-2 text-[16px]" data-testid="need-sooner">Need it sooner? Text <a className="underline" href={`sms:${store.phone.replace(/[^\d+]/g, "")}`}>{store.phone}</a>.</p>
            )}
        </Section>
    );
}

/** 8.13 Opening soon — a live shop whose Stripe can't take charges yet: the grid stays, the Pay button becomes this line. */
export function OpeningSoon({ store, testid = "opening-soon" }) {
    const phone = store?.phone || "";
    return (
        <div className="mt-4 border border-ink px-3 py-3 text-[16px]" data-testid={testid}>
            <p className="font-semibold">Opening soon.</p>
            <p>Checkout opens when payments are set up.</p>
            {phone && <p className="mt-1">Call or text <a className="underline" href={`sms:${phone.replace(/[^\d+]/g, "")}`} data-testid={`${testid}-phone`}>{phone}</a>.</p>}
        </div>
    );
}

/** 5.1 #7 — one big all-in price, one primary button, Add to cart as a text link. */
export function PriceBar({ total, note, errors = [], busy, onPay, onAdd, pickup, store }) {
    const blocked = busy || errors.length > 0;
    const mall = useMall();
    const shop = store || mall.store;
    const openingSoon = shop && shop.accepts_cards === false;
    return (
        <Section testid="price-section">
            <p className="text-[28px] font-semibold leading-none" data-testid="line-total">{money(total)}</p>
            {note && <p className="mt-1 text-[14px] text-muted-ink" data-testid="price-note">{note}</p>}
            {errors.length > 0 && !openingSoon && <p className="mt-2 text-[16px]" style={{ color: "var(--error)" }} data-testid="price-errors">{errors[0]}</p>}
            {pickup}
            {openingSoon ? <OpeningSoon store={shop} /> : (
                <>
                    <button type="button" onClick={onPay} disabled={blocked} className={`${btnPrimary} mt-4`} data-testid="pay-now">
                        {busy ? "Working…" : "Pay now"}
                    </button>
                    <button type="button" onClick={onAdd} disabled={blocked} className="mt-3 block min-h-[44px] w-full text-center text-[16px] underline disabled:opacity-50" data-testid="add-to-cart">
                        Add to cart
                    </button>
                </>
            )}
        </Section>
    );
}

export function NotFound({ store }) {
    return (
        <div className="px-3 py-10" data-testid="simple-product-missing">
            <h1 className="text-[28px] font-semibold">We don't print that one here</h1>
            <p className="mt-3"><Link to={store?.slug ? `/stores/${store.slug}` : "/"} className="underline">Back to the grid</Link></p>
        </div>
    );
}

/** The effective DPI check — pixels divided by the size it will print at. */
export const dpiCheck = (file, widthIn) => {
    if (!file) return null;
    const px = Number(file.width_px || file.px?.w || 0);
    if (!px || !widthIn) return { ok: false, dpi: null };
    const dpi = Math.floor(px / Number(widthIn));
    return { ok: dpi >= 150, dpi };
};
